import { track } from "@amplitude/analytics-react-native";
import { useCallback } from "react";
import GetGatheringShareInfoDocument from "src/api/graphql/__generated__/documents/GetGatheringShareInfoDocument";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import { useAsyncQuery } from "src/api/graphql/helper";
import { SharePayloadData } from "src/components/ShareMenu/types";
import { useGatheringStore } from "src/contexts/GatheringContext";
import formatGatheringMetadataTitle from "src/helpers/gatheringHelpers/formatGatheringMetadataTitle";
import log from "src/helpers/log";
import { useModal } from "src/modals";
import GlobalModal from "src/modals/configs/GlobalModal";

const useShareGathering = ({ amplitudeEvent }: { amplitudeEvent?: string } = {}) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const { presentModal } = useModal<GlobalModal>();
	const getGatheringShareInfo = useAsyncQuery(GetGatheringShareInfoDocument);

	const handleShareGathering = useCallback(
		async ({ variant = "Default" }: { variant?: "Default" | "Create" } = {}) => {
			const [{ data }] = await Promise.all([
				getGatheringShareInfo({
					variables: {
						input: {
							gatheringId,
						},
					},
					fetchPolicy: "network-only",
				}),
			]);
			const { shareUrl, privacy, password, name } = data?.getGathering ?? {};

			const { shareString, shareType } = (() => {
				if (!shareUrl) {
					log.error({ gatheringId }, "Gathering share: shareUrl is undefined");
					return { shareString: null, shareType: null };
				}
				if (privacy?.includes(GatheringPrivacy.PasswordRequired)) {
					if (!password) {
						log.error({ gatheringId }, "Gathering share: Missing expected password");
						return { shareString: null, shareType: null };
					}
					if (!name) {
						log.error({ gatheringId }, "Gathering share: Missing expected name");
						return { shareString: null, shareType: null };
					}
					return {
						shareString: `${shareUrl}\nJoin me in ${name}!\n\nYour password is ${password}`,
						shareType: "Text",
						openGraphTitle: formatGatheringMetadataTitle(name),
					};
				}
				return {
					shareString: shareUrl,
					shareType: "Url",
					openGraphTitle: formatGatheringMetadataTitle(name),
				};
			})() satisfies
				| {
						shareString: string;
						shareType: "Text" | "Url";
				  }
				| {
						shareString: null;
						shareType: null;
				  };
			if (!shareString) return;
			if (amplitudeEvent) {
				track(amplitudeEvent);
			}
			presentModal("Share", {
				closeOnHookUnmount: false,
				props: {
					data: [
						shareType === "Text"
							? {
									type: "Text",
									text: shareString,
								}
							: {
									type: "Url",
									url: shareString,
								},
					] satisfies SharePayloadData,
					header: {
						title: "Share album",
						subTitle: "Anyone with the link can join!",
					},
					closeVariant: variant === "Create" ? "Skip" : "Close",
				},
			});
		},
		[getGatheringShareInfo, gatheringId, amplitudeEvent, presentModal],
	);
	return {
		handleShareGathering,
	};
};
export default useShareGathering;
